/**
 * Environment profile client (lane 4070): profile versions, their sections, the destination
 * behavior of one attribute with its source, and the Maximo Base Pack discrepancies.
 *
 * Everything here reads a sealed, immutable profile version. The only writes are a Base Pack
 * comparison and a human decision on a discrepancy; the API authorizes both.
 */
import { ApiError, apiFetch } from "../../app/api";

const BASE = "/api/v1/environment-profiles";

export type Status = "profiled" | "absent" | "unknown";
export type OriginKind = "extension" | "customer_defined" | "product";
export type Row = Record<string, unknown>;

export interface ProfileVersionRef {
  snapshot_id: string;
  environment_profile_id: string;
  profile_key: string;
  profile_version: number;
  content_sha256: string;
  seal_sha256: string;
}

export interface EnvironmentSummary {
  id: string;
  profile_key: string;
  environment_name: string;
  environment_role: "production" | "nonproduction" | "simulator";
  profile_state: "declared" | "captured" | "superseded";
  snapshot_count: number;
  current: ProfileVersionRef | null;
}

export type SnapshotStatus = "loading" | "sealed" | "superseded" | "withdrawn" | "abandoned";

/** Only a sealed or superseded profile version can be browsed, traced or compared; the API
 * refuses the others (environment._pinned). Their source can still be shown. */
export const BROWSABLE: ReadonlySet<SnapshotStatus> = new Set(["sealed", "superseded"]);

export interface EnvironmentSnapshot {
  id: string;
  environment_profile_id: string;
  profile_key: string;
  environment_name: string;
  environment_role: string;
  profile_version: number;
  status: SnapshotStatus;
  collection_method: string;
  collection_source: string;
  collected_at: string;
  collected_by: string;
  source_sha256: string;
  source_instance_id: string;
  platform: string;
  maximo_version: string;
  platform_version: string | null;
  database_platform: string | null;
  capture_coverage: "complete" | "partial";
  coverage_statement: string | null;
  content_sha256: string | null;
  seal_sha256: string | null;
  sealed_at: string | null;
  superseded_by: string | null;
}

export interface Component {
  kind: string;
  code: string;
  display_name: string;
  version: string | null;
}

export interface ScopeEntry {
  kind: "organization" | "site" | "item_set" | "company_set";
  code: string;
  organization_code: string | null;
  active: boolean;
  description: string | null;
}

export interface SnapshotDetail {
  snapshot: EnvironmentSnapshot;
  components: Component[];
  scope: ScopeEntry[];
}

export interface SectionCoverage {
  section: string;
  captured: boolean;
  row_count: number;
}

export interface Finding {
  code: string;
  severity: "info" | "warning";
  section: string | null;
  subject: string;
  message: string;
}

export interface SnapshotCoverage {
  snapshot_id: string;
  capture_coverage: "complete" | "partial";
  coverage_statement: string | null;
  sections: SectionCoverage[];
  findings: Finding[];
}

export interface ElementOrigin {
  origin: OriginKind;
  basis: string;
  component_code: string | null;
  component_kind: string | null;
  rule_prefix: string | null;
}

export interface SourceReference {
  profile_version: string;
  snapshot_id: string;
  seal_sha256: string;
  source_sha256: string;
  collection_method: string;
  collection_source: string;
  section: string;
  row_key: string;
}

export interface MetadataRow {
  row_key: string;
  data: Row;
  origin: ElementOrigin | null;
}

export interface MetadataPage {
  snapshot_id: string;
  profile_version: string;
  section: string;
  status: Status;
  coverage: SectionCoverage | null;
  total: number;
  offset: number;
  limit: number;
  rows: MetadataRow[];
}

export interface MetadataQuery {
  q?: string;
  offset?: number;
  limit?: number;
  objectname?: string;
  domainid?: string;
  classstructureid?: string;
  intobjectname?: string;
}

export interface CustomElement {
  kind: string;
  name: string;
  objectname: string | null;
  origin: ElementOrigin;
  definition: Row;
  scope_level: string | null;
  support_level: string;
  source: SourceReference;
}

export interface ObjectBehavior {
  status: Status;
  objectname: string;
  reason: string | null;
  definition: Row | null;
  origin: ElementOrigin | null;
  scope_level: string | null;
  scope_dimensions: string[] | null;
  key_columns: string[];
  source: SourceReference | null;
}

export interface DomainBehavior {
  status: Status;
  domainid: string;
  reason: string | null;
  definition: Row | null;
  values_status: Status;
  values: Row[];
  scope: Record<string, string | null>;
  valid_in_scope: string[];
  source: SourceReference | null;
}

export interface AttributeBehavior {
  status: Status;
  objectname: string;
  attributename: string;
  reason: string | null;
  definition: Row | null;
  origin: ElementOrigin | null;
  support_level: string | null;
  object: ObjectBehavior;
  writability: "writable" | "not_writable" | "unknown";
  domain: DomainBehavior | null;
  same_as: string | null;
  key_position: number | null;
  security_restrictions: Row[];
  automation: Row[];
  source: SourceReference | null;
}

export type Category =
  | "type"
  | "requiredness"
  | "persistence"
  | "domain"
  | "relationship"
  | "version"
  | "add_on"
  | "custom";
export type DiscrepancyStatus = "open" | "acknowledged" | "resolved";
export type Action = "acknowledge" | "resolve" | "reopen";
export type Resolution =
  | "environment_intended"
  | "pack_correction_proposed"
  | "profile_recapture_required"
  | "not_material";

export interface Populations {
  pack_elements: number;
  compared: number;
  consistent: number;
  differing: number;
  missing_in_profile: number;
  not_comparable: number;
  not_applicable: number;
  not_covered_by_pack: number;
  not_comparable_reason: string | null;
}

export interface Comparison {
  id: string;
  snapshot_id: string;
  profile_version: string;
  pack_key: string;
  pack_version: string;
  pack_manifest_sha256: string;
  ruleset_version: string;
  summary: {
    profile_maximo_version: string;
    pack_targets: string[];
    sections: Record<string, Populations>;
    discrepancies: number;
    by_category: Record<string, number>;
  };
  compared_by: string;
  compared_at: string;
}

export interface PriorDecision {
  discrepancy_id: string;
  profile_version: string;
  pack_version: string;
  status: DiscrepancyStatus;
  resolution: Resolution | null;
  decided_by: string;
  decided_at: string;
  reason: string;
}

export interface Discrepancy {
  id: string;
  comparison_id: string;
  category: Category;
  difference: string;
  element_kind: string;
  element: string;
  profile_value: unknown;
  pack_value: unknown;
  profile_section: string | null;
  profile_row_key: string | null;
  pack_entry_id: string | null;
  governing: "environment_profile";
  severity: "info" | "warning";
  message: string;
  status: DiscrepancyStatus;
  resolution: Resolution | null;
  prior: PriorDecision | null;
}

export interface Decision {
  id: number;
  from_status: DiscrepancyStatus;
  action: Action;
  to_status: DiscrepancyStatus;
  resolution: Resolution | null;
  decided_by: string;
  acting_role: string | null;
  reason: string;
  decided_at: string;
}

export interface HistoryEntry {
  comparison_id: string;
  profile_version: string;
  pack_version: string;
  compared_at: string;
  discrepancy: Discrepancy;
  decisions: Decision[];
}

export interface DiscrepancyDetail {
  discrepancy: Discrepancy;
  decisions: Decision[];
  history: HistoryEntry[];
}

export interface GenericReference {
  pack_key: string;
  pack_version: string;
  pack_version_id: string;
  entry_id: string;
  definition: Row;
  source_ids: string[];
}

export interface GoverningAttribute {
  governed_by: "environment_profile";
  objectname: string;
  attributename: string;
  profile: AttributeBehavior;
  generic_status: "referenced" | "not_in_pack" | "no_pack";
  generic_reference: GenericReference | null;
  comparison_id: string | null;
  discrepancies: Discrepancy[];
}

export interface Scope {
  orgid?: string;
  siteid?: string;
}

function query(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const part = encodeURIComponent;

export function listEnvironments(): Promise<EnvironmentSummary[]> {
  return apiFetch(BASE);
}

export function environmentSnapshots(environmentId: string): Promise<EnvironmentSnapshot[]> {
  return apiFetch(`${BASE}/${part(environmentId)}/snapshots`);
}

export function snapshotDetail(snapshotId: string): Promise<SnapshotDetail> {
  return apiFetch(`${BASE}/snapshots/${part(snapshotId)}`);
}

export function snapshotCoverage(snapshotId: string): Promise<SnapshotCoverage> {
  return apiFetch(`${BASE}/snapshots/${part(snapshotId)}/coverage`);
}

export function browseMetadata(
  snapshotId: string,
  section: string,
  params: MetadataQuery = {},
): Promise<MetadataPage> {
  return apiFetch(
    `${BASE}/snapshots/${part(snapshotId)}/metadata/${part(section)}${query(params)}`,
  );
}

export function customElements(snapshotId: string): Promise<CustomElement[]> {
  return apiFetch(`${BASE}/snapshots/${part(snapshotId)}/custom-elements`);
}

export function governingAttribute(
  snapshotId: string,
  objectname: string,
  attributename: string,
  scope: Scope = {},
): Promise<GoverningAttribute> {
  return apiFetch(
    `${BASE}/snapshots/${part(snapshotId)}/objects/${part(objectname)}/attributes/` +
      `${part(attributename)}/governing${query(scope)}`,
  );
}

export function packComparisons(snapshotId: string): Promise<Comparison[]> {
  return apiFetch(`${BASE}/snapshots/${part(snapshotId)}/pack-comparisons`);
}

export function runComparison(snapshotId: string, reason: string): Promise<Comparison> {
  return apiFetch(`${BASE}/snapshots/${part(snapshotId)}/pack-comparisons`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function comparisonDetail(
  comparisonId: string,
  filters: { category?: Category; status?: DiscrepancyStatus } = {},
): Promise<{ comparison: Comparison; discrepancies: Discrepancy[] }> {
  return apiFetch(`${BASE}/pack-comparisons/${part(comparisonId)}${query(filters)}`);
}

export function discrepancyDetail(discrepancyId: string): Promise<DiscrepancyDetail> {
  return apiFetch(`${BASE}/discrepancies/${part(discrepancyId)}`);
}

export function decide(
  discrepancyId: string,
  decision: { action: Action; resolution?: Resolution; reason: string },
): Promise<Discrepancy> {
  return apiFetch(`${BASE}/discrepancies/${part(discrepancyId)}/decisions`, {
    method: "POST",
    body: JSON.stringify(decision),
  });
}

/** A value as the profile states it: "none" for null, JSON otherwise. */
export function shown(value: unknown): string {
  if (value === null || value === undefined) return "none";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** The trace route of one attribute of a profile version. */
export function tracePath(snapshotId: string, objectname: string, attributename: string): string {
  return (
    `/environment-profiles/snapshots/${part(snapshotId)}/objects/${part(objectname)}` +
    `/attributes/${part(attributename)}`
  );
}

export type Impact = "material" | "nonmaterial" | "unknown";
export type ItemAction = "requalified" | "not_affected" | "reopen";
export type ItemStatus = "open" | "requalified" | "not_affected";

export interface FieldChange {
  field: string;
  before: unknown;
  after: unknown;
  area: string;
  impact: Impact;
  explanation: string;
}

export interface ElementChange {
  section: string;
  row_key: string;
  element: string;
  kind: "added" | "removed" | "changed" | "coverage";
  impact: Impact;
  areas: string[];
  fields: FieldChange[];
  explanation: string;
}

export interface ChangeSet {
  id: string;
  environment_profile_id: string;
  from_snapshot_id: string;
  from_version: string;
  to_snapshot_id: string;
  to_version: string;
  rules_version: string;
  material: boolean;
  summary: {
    items: number;
    material: number;
    nonmaterial: number;
    unknown: number;
    historical_release_batches?: number;
  };
  changes: ElementChange[];
  created_at: string;
}

export interface RequalificationItem {
  id: string;
  change_set_id: string;
  item_kind: string;
  item_id: string;
  label: string;
  reasons: string[];
  details: Row;
  status: ItemStatus;
}

/** The recorded change set into a profile version, or null for a first version. */
export async function changeSetInto(
  snapshotId: string,
): Promise<{ change_set: ChangeSet; items: RequalificationItem[] } | null> {
  try {
    return await apiFetch(`${BASE}/snapshots/${part(snapshotId)}/change-set`);
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 404) return null;
    throw caught;
  }
}

export function decideItem(
  itemId: string,
  decision: { action: ItemAction; reason: string },
): Promise<RequalificationItem> {
  return apiFetch(`${BASE}/requalification-items/${part(itemId)}/decisions`, {
    method: "POST",
    body: JSON.stringify(decision),
  });
}
